import { apiPost, apiGet, apiDelete, apiPutMultipart, apiPostMultipart } from 'helpers/ApiHelper';
import Auth from 'j-toker';
import * as notificationsActions from './notificationActions';

import * as formatter from 'helpers/Formatter';
import { deepDiver } from 'helpers/utilities';

export function getNotices(offset = 0) {
  return (dispatch) => {
    return dispatch({
      payload: apiGet(`${Auth.getApiUrl()}/landlords/notices?offset=${offset}`)
        .done((response) => {
          dispatch(showNotices(response.notices, offset));
        })
        .catch((error, textStatus) => {
          dispatch(notificationsActions.showFailure(error));
          console.error(textStatus);
        }),
    });
  };
}

export function getNoticesStats() {
  const route = '/landlords/notices_stats';
  return (dispatch) => {
    return dispatch({
      type: 'LANDLORD_NOTICES::FETCH_NOTICES_STATS',
      payload: apiGet(Auth.getApiUrl() + route).done((response) => {
        dispatch({
          type: 'LANDLORD_NOTICES::SAVE_NOTICES_STATS',
          payload: response.detailed_notices_counts,
        });
      }),
    });
  };
}

export function getFilteredNotices(subset = 'all', offset = 0, options = {}) {
  return (dispatch) => {
    return dispatch({
      payload: apiPost(
        `${Auth.getApiUrl()}/landlords/filtered_notices?subset=${subset}&offset=${offset}`,
        options
      )
        .done((response) => {
          dispatch(showNotices(response.notices, offset));
        })
        .catch((error, textStatus) => {
          dispatch(notificationsActions.showFailure(error));
          console.error(textStatus);
        }),
    });
  };
}

export function showNotices(noticesResponse, offset) {
  const notices = noticesResponse.map((notice) => {
    let expiration_date;
    if (notice.expiration_date) {
      expiration_date = formatter.toDate(notice.expiration_date);
    }
    let post_date;
    if (notice.post_date) {
      post_date = formatter.toDate(notice.post_date);
    }

    return {
      ...notice,
      expiration_date,
      post_date,
    };
  });

  return {
    type: 'LANDLORD_ANNOUNCEMENTS::SHOW_ANNOUNCEMENTS',
    notices,
    offset,
  };
}

export function getNoticeDeliveryReport(noticeId) {
  return (dispatch) => {
    return dispatch({
      payload: apiGet(Auth.getApiUrl() + `/landlords/notices/${noticeId}/delivery_reports`)
        .done((response) => {
          dispatch({
            type: 'LANDLORD_NOTICES::SHOW_DELIVERY_REPORTS',
            delivery_report: response.delivery_report,
          });
        })
        .catch((error) => {
          dispatch(notificationsActions.showFailure(error));
          console.error('request failed', error);
        }),
    });
  };
}

export function getNoticeDetails(noticeId) {
  return (dispatch) => {
    return dispatch({
      payload: apiGet(Auth.getApiUrl() + `/landlords/notices/${noticeId}`)
        .done((response) => {
          let notice = response.notice_details;
          let expiration_date;
          if (notice.expiration_date) {
            expiration_date = formatter.toDate(notice.expiration_date);
          }
          let post_date;
          if (notice.post_date) {
            post_date = formatter.toDate(notice.post_date);
          }

          dispatch({
            type: 'LANDLORD_NOTICES::SAVE_NOTICE_DETAILS',
            noticeDetails: {
              ...notice,
              expiration_date,
              post_date,
            },
          });
        })
        .catch((error) => {
          dispatch(notificationsActions.showFailure(error));
          console.error('request failed', error);
        }),
    });
  };
}

export function downloadGenericPdf(noticeId) {
  return (dispatch) => {
    return dispatch({
      payload: apiPost(Auth.getApiUrl() + `/landlords/notices/${noticeId}/download_generic_pdf`)
        .done((response) => {
          window.open(response.pdf_url, '_blank');
        })
        .catch((error) => {
          dispatch(notificationsActions.showFailure(error));
          console.error('request failed', error);
        }),
    });
  };
}

export function downloadTargetedPdf(noticeId, propertyIds) {
  return (dispatch) => {
    return dispatch({
      payload: apiPost(Auth.getApiUrl() + `/landlords/notices/${noticeId}/download_targeted_pdf`, {
        property_ids: propertyIds,
      })
        .done((response) => {
          window.open(response.pdf_url, '_blank');
        })
        .catch((error) => {
          dispatch(notificationsActions.showFailure(error));
          console.error('request failed', error);
        }),
    });
  };
}

export function createOrUpdateNotice(noticeId, data) {
  return (dispatch) => {
    const multiPartFormData = new FormData();

    if (deepDiver(data, 'image.0.lastModified')) {
      multiPartFormData.append('image', data.image[0]);
    }

    if (deepDiver(data, 'attachments')) {
      data['attachments'].forEach((attachment, i) => {
        let appendable = attachment;
        if ('id' in attachment) {
          appendable = attachment.id;
        }
        multiPartFormData.append('attachments[]', appendable);
      });
    }

    data = JSON.parse(JSON.stringify(data));

    if (Array.isArray(data['properties'])) {
      data['property_ids'] = data['properties'].map((property) => {
        return property.value;
      });
    } else {
      data['property_ids'] = [data['properties'].value];
    }

    if (Array.isArray(data['units'])) {
      data['unit_ids'] = data['units'].map((unit) => {
        return unit.value;
      });
    } else {
      data['unit_ids'] = [data['units'].value];
    }

    if (Array.isArray(data['cotenants'])) {
      data['cotenant_ids'] = data['cotenants'].map((cotenant) => {
        return cotenant.value;
      });
    } else {
      data['cotenant_ids'] = [data['cotenants'].value];
    }

    if (data['floors']) {
      data['floor_names'] = data['floors'].map((floor) => {
        return floor.value;
      });
    }

    if (data['risers']) {
      data['riser_names'] = data['risers'].map((riser) => {
        return riser.value;
      });
    }

    if (data['expiration_date']) {
      data['expiration_date'] = formatter.toISO8601DateString(data['expiration_date']);
    }

    if (data['effective_dates']) {
      data['effective_dates'] = data['effective_dates'].map((effectiveDates) => {
        return effectiveDates.map((effectiveDate) => {
          return formatter.toISO8601DateTimeString(effectiveDate);
        });
      });
    }

    data['post_date'] = formatter.toISO8601DateTimeString(data['post_date']);

    const formData = JSON.stringify(data);
    multiPartFormData.append('data', formData);

    return dispatch({
      payload: noticePayload(noticeId, multiPartFormData)
        .done((response) => {
          dispatch(notificationsActions.showSuccess('noticeSuccessfullyCreated'));
          dispatch(getNotices());
        })
        .catch((error) => {
          dispatch(notificationsActions.showFailure(error));
          console.error('request failed', error);
        }),
    });
  };
}

function noticePayload(noticeId, multiPartFormData) {
  if (noticeId) {
    return apiPutMultipart(Auth.getApiUrl() + `/landlords/notices/${noticeId}`, multiPartFormData);
  } else {
    return apiPostMultipart(Auth.getApiUrl() + '/landlords/notices', multiPartFormData);
  }
}

export function deleteNotice(noticeId) {
  return (dispatch) => {
    return dispatch({
      payload: apiDelete(Auth.getApiUrl() + `/landlords/notices/${noticeId}`)
        .done((response) => {
          dispatch(notificationsActions.showSuccess('noticeSuccessfullyDeleted'));
        })
        .catch((error, textStatus) => {
          dispatch(notificationsActions.showFailure(error));
          console.error(textStatus);
        }),
    });
  };
}

export function saveSignature(signature) {
  return {
    type: 'LANDLORD_ANNOUNCEMENTS::SAVE_SIGNATURE',
    signature,
  };
}

export function getNoticeTemplates() {
  return (dispatch) => {
    return dispatch({
      payload: apiGet(Auth.getApiUrl() + `/landlords/notice_templates`)
        .done((response) => {
          dispatch({
            type: 'LANDLORD_NOTICES::SHOW_NOTICE_TEMPLATES',
            notice_templates: response.notice_templates,
          });
        })
        .catch((error) => {
          dispatch(notificationsActions.showFailure(error));
          console.error('request failed', error);
        }),
    });
  };
}

export function postNoticeTemplate(data) {
  const multiPartFormData = new FormData();

  if (deepDiver(data, 'notice_image.0.lastModified')) {
    multiPartFormData.append('notice_image', data.notice_image[0]);
  }

  if (deepDiver(data, 'attachments')) {
    data['attachments'].forEach((attachment, i) => {
      let appendable = attachment;
      if ('id' in attachment) {
        appendable = attachment.id;
      }
      multiPartFormData.append('attachments[]', appendable);
    });
  }
  data = JSON.parse(JSON.stringify(data));

  data['property_ids'] = [];
  if (data['properties']) {
    data['property_ids'] = data['properties'].map((property) => {
      return property.value;
    });
  }

  data['allow_attachment_modify'] = data['allow_attachment_modify'] === true;
  data['allow_image_modify'] = data['allow_image_modify'] === true;

  const formData = JSON.stringify(data);

  multiPartFormData.append('data', formData);

  return (dispatch) => {
    return dispatch({
      payload: apiPostMultipart(Auth.getApiUrl() + `/landlords/notice_templates`, multiPartFormData)
        .done((response) => {
          dispatch(notificationsActions.showSuccess('noticeTemplateSuccessfullyCreated'));
        })
        .catch((error) => {
          dispatch(notificationsActions.showFailure(error));
          console.error('request failed', error);
        }),
    });
  };
}

export function putNoticeTemplate(id, data) {
  const multiPartFormData = new FormData();

  if (deepDiver(data, 'notice_image.0.lastModified')) {
    multiPartFormData.append('notice_image', data.notice_image[0]);
  }

  if (deepDiver(data, 'attachments')) {
    data['attachments'].forEach((attachment, i) => {
      let appendable = attachment;
      if ('id' in attachment) {
        appendable = attachment.id;
      }
      multiPartFormData.append('attachments[]', appendable);
    });
  }

  data = JSON.parse(JSON.stringify(data));

  data['property_ids'] = data['properties'].map((property) => {
    return property.value;
  });

  data['allow_attachment_modify'] = data['allow_attachment_modify'] === true;
  data['allow_image_modify'] = data['allow_image_modify'] === true;

  const formData = JSON.stringify(data);
  multiPartFormData.append('data', formData);

  return (dispatch) => {
    return dispatch({
      payload: apiPutMultipart(
        Auth.getApiUrl() + `/landlords/notice_templates/${id}`,
        multiPartFormData
      )
        .done((response) => {
          dispatch(notificationsActions.showSuccess('noticeTemplateSuccessfullyUpdated'));
        })
        .catch((error) => {
          dispatch(notificationsActions.showFailure(error));
        }),
    });
  };
}

export function deleteNoticeTemplate(noticeTemplateId) {
  return (dispatch) => {
    return dispatch({
      payload: apiDelete(Auth.getApiUrl() + `/landlords/notice_templates/${noticeTemplateId}`)
        .done((response) => {
          dispatch(notificationsActions.showSuccess('noticeTemplateSuccessfullyDeleted'));
        })
        .catch((error) => {
          dispatch(notificationsActions.showFailure(error));
          console.error('request failed', error);
        }),
    });
  };
}
